import {
    AlertColor,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Modal,
    styled,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React, { useImperativeHandle } from "react";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { RegionPermission, Staff } from "./StaffAccessories";
import { renewToken } from "../../../services/AuthService";
import { OpenMode } from "../../../common/Enums";
import { GeneralDialog } from "../../component/GeneralDialog";
import { CommonProps } from "../../component/MainContents";
import { DataGridPro, GridColumns, GridRowId } from "@mui/x-data-grid-pro";
import { DropDownComboOption } from "../../component/GenericFilter";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
    })
);

export interface StaffSingleHandle {
    Initialise: (mode: OpenMode, staff: Staff) => void;
}

export const StaffSingle = React.forwardRef(
    (props: CommonProps, ref: React.Ref<StaffSingleHandle>) => {
        const style = { ...useStyles(), ...globalTheme() };

        const tinyScreen = useMediaQuery('(max-width:1366px)');
        const smallScreen = useMediaQuery('(max-width:1920px)');
        const normalScreen = useMediaQuery('(max-width:2560px)');

        const [open, setOpen] = React.useState(false);
        const [openMode, setOpenMode] = React.useState<OpenMode>(OpenMode.NEW);
        const [loading, setLoading] = React.useState<boolean>(false);
        const [hasError, setHasError] = React.useState(false);
        const [staff, setStaff] = React.useState<Staff>();
        const [originalStaff, setOriginalStaff] = React.useState<Staff>();
        const [saved, setSaved] = React.useState<boolean>(false);

        //Dialogs and snacks
        const hasChanges = React.useRef<boolean>(false);
        const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
        const [snackSettings, setSnackSettings] =
        React.useState<SnackBarMessageProps>({
            open: false,
            message: "",
            subMessage: "",
            severity: undefined,
            onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });

        function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
            setSnackSettings({
                open: true,
                message: message,
                subMessage: subMessage,
                severity: colour,
                onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
            });
        }

        const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
            "& .MuiDataGrid-columnHeaders": { display: "none" },
            "& .MuiDataGrid-virtualScroller": { marginTop: "0!important" }
          }));
          
        const [gridData, setGridData] = React.useState<DropDownComboOption[]>([]);

        //Grid columns
        const columns: GridColumns = [
          { field: 'value', headerName: 'Region', flex: 1, type: 'string', editable: false }
        ];
        
        const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>([]);

        const selectedData = React.useRef<DropDownComboOption[]>([]);

        useImperativeHandle(ref, () => ({
            Initialise(mode: OpenMode, staff: Staff) {
                setOpen(true);
                setSaved(false);
                renewToken();

                axios.get<any[]>(`Api/V1/Branch`, {
                    headers: {
                        'Content-Encoding': 'gzip'
                    }
                })
                    .then((result) => {
                        let loadedRegions: DropDownComboOption[] = [];
                        result.data.map(r => loadedRegions.push({ name: r.region, value: r.region }));

                        setGridData(loadedRegions);

                        if (staff.regionPermissions)
                        {
                            const regions:GridRowId[] = [];
                            staff.regionPermissions.forEach(x => regions.push(x.region));
                            setSelectionModel(regions);
                            selectedData.current = staff.regionPermissions.map(x => {
                                const regionPermission: DropDownComboOption = {name: x.region, value: x.region};
                                return regionPermission;
                            });
                        }
                        else
                        {
                            setSelectionModel([]);
                            selectedData.current = [];
                        }
        
                        hasChanges.current = false;
                        setOpenMode(mode);
        
                        if (staff)
                        {
                            setStaff(staff);
                            setOriginalStaff(staff);
                        }
                    },
                        (error) => {
                            console.log(error);
                            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    )
                    .catch((error) => {
                        console.log(error);
                        showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                    });
            },
        }));

        function closeModal() {
            setDialogOpen(false);
            setOpen(false);
            props.onClose(saved, originalStaff);
        }

        function closeModalCheck(keepOpen: boolean) {
            if (hasChanges.current) {
              setDialogOpen(true);
            } else {
              setDialogOpen(false);
              setOpen(keepOpen);
              props.onClose(saved, originalStaff);
            }
        }

        function OnStaffChanged(
            propertyName: string,
            propertyValue: any,
            validationRegex?: RegExp
        ) {
            let proceed = true;
        
            if (validationRegex !== undefined) {
                proceed = validationRegex.test(propertyValue);
            }
        
            if (proceed) {
                let temp: any = { ...staff };
        
                if (temp[propertyName] != propertyValue)
                {
                    hasChanges.current = true;
                    temp[propertyName] = propertyValue ?? null;
                    setStaff(temp);
                }
            }
        }

        function OnIsActiveChecked(event: React.ChangeEvent<HTMLInputElement>) {
            let temp: any = { ...staff };
            temp.isActive = event.target.checked;
            setStaff(temp);
            hasChanges.current = true;
        }
        
        function SaveStaff(close: boolean) {
            if (hasChanges.current)
            {
                if (staff?.loginName == null || staff?.loginName == '')
                {
                    showSnackBar("Please enter a login name for the user.", "", "error");
                    return;
                }
                if (staff?.region == null || staff?.region == '')
                {
                    showSnackBar("Please select a region for the user.", "", "error");
                    return;
                }

                let tempStaff = {...staff};
                tempStaff.defaultPage = tempStaff.defaultPage ?? "";
                tempStaff.password = '';
                tempStaff.permissions = [];
                tempStaff.regionPermissions = selectedData.current.map(x => {
                    const regionPermission: RegionPermission = {region: x.value, loginName: tempStaff.loginName ?? ""};
                    return regionPermission;
                });
                if (openMode === OpenMode.EDIT)
                {
                    axios.put<Staff>('Api/V1/Staff/', tempStaff).then(
                        (result) => {
                            showSnackBar("Staff saved successfully.", "", "success");
                            hasChanges.current = false;
                            setSaved(true);
                            setStaff(result.data);
                            setOriginalStaff(result.data);
                            setOpen(!close);
                            setDialogOpen(false);
                            if (close)
                                props.onClose(true, result.data);
                        },
                        (error) => {
                            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    );
                } 
                else
                {
                    axios.post<Staff>('Api/V1/Staff/', tempStaff).then(
                        (result) => {
                            showSnackBar("Staff created successfully.", "", "success");
                            hasChanges.current = false;
                            setSaved(true);
                            setStaff(result.data);
                            setOriginalStaff(result.data);
                            setOpenMode(OpenMode.EDIT);
                            setOpen(!close);
                            setDialogOpen(false);
                            if (close)
                                props.onClose(true, result.data);
                        },
                        (error) => {
                            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    );
                }
            }
        }

        return (
            <div>
                <Modal open={open} onClose={() => closeModalCheck(true)}>
                    <div className={style.ModalMainNarrow}>
                        {!loading ? (
                            <div className={style.ModalBody}>
                                <Title pageTitle="Staff" />
                                <div className={style.ModalContent}>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldOneQuarterWidth} style={{marginTop: 8}}
                                            label="Region"
                                            value={staff?.region ?? ""}
                                            onChange={(e) => {
                                                if (e.target.value != "-1")
                                                {
                                                    OnStaffChanged("region", e.target.value)
                                                    let selectedRows = selectionModel;
                                                    if (selectedRows.findIndex(x => x == e.target.value) == -1)
                                                    {
                                                        selectedRows.push(e.target.value);
                                                        selectedData.current.push({name: e.target.value, value: e.target.value});
                                                    }
                                                    setSelectionModel(selectedRows);
                                                }
                                            }}
                                            select
                                            size="small"
                                            fullWidth
                                        >
                                            {props.regions.map((item)=> (
                                                <MenuItem key={item.value} value={item.value}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <div className={style.ModalContentFieldOneQuarterWidth}>
                                            <FormControlLabel
                                                control={<Checkbox checked={staff?.isActive ?? false} onChange={(e) => OnIsActiveChecked(e)} />}
                                                label="Is Active"
                                            />
                                        </div>
                                        <TextField className={style.ModalContentFieldHalfWidth}
                                            label="Title"
                                            value = {staff?.title ?? ""}
                                            onChange={(e) => OnStaffChanged("title", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                       <TextField className={style.ModalContentFieldHalfWidth}
                                            label="Staff Name"
                                            value = {staff?.staffName ?? ""}
                                            onChange={(e) => OnStaffChanged("staffName", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldHalfWidth}
                                            label="Login Name"
                                            value = {staff?.loginName ?? ""}
                                            InputProps={{
                                                readOnly: openMode != OpenMode.NEW,
                                            }}
                                            onChange={(e) => OnStaffChanged("loginName", e.target.value.slice(0, 30))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <div className={style.ModalContentFieldFullWidth} style={{minWidth: 100, minHeight: "200px"}}>
                                            <StyledDataGrid
                                                getRowId={(row) => row.value}
                                                rows={gridData}
                                                columns={columns}
                                                loading={loading}
                                                checkboxSelection
                                                selectionModel = {selectionModel}
                                                onSelectionModelChange={(selections) => {
                                                  setSelectionModel(selections)
                                                  const ids = new Set(selections);
                                                  const selectedRows = gridData.filter((row) => ids.has(row.value));
                                                  selectedData.current = selectedRows;
                                                  hasChanges.current = true;
                                                }}
                                                hideFooter
                                                autoHeight
                                                rowHeight={tinyScreen ? 20 : (smallScreen ? 24 : 28)}
                                            />
                                        </div>                                    
                                    </div>
                                </div>

                                <div className={style.ModalFooter}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className={style.margin}
                                        onClick={(e) => {
                                                SaveStaff(false);
                                            }
                                        }
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Save
                                    </Button>
                                    <div style={{ width: `100%`, minWidth: "10" }}></div>
                                    <Button
                                        variant="contained"
                                        className={style.margin}
                                        onClick={(e) => closeModalCheck(false)}
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Close
                                    </Button>                                
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: "center" }}>
                                <CircularProgress color="primary" />
                            </div>
                        )}
                    </div>
                </Modal>

                <SnackBarMessage {...snackSettings} />

                <GeneralDialog
                    title={"Save Changes?"}
                    message={
                        "Changes have been made, did you want to save before closing?"
                    }
                    open={dialogOpen}
                    options={[
                        {
                        text: "Save",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => SaveStaff(true),
                        color: "secondary",
                        variant: "contained",
                        },
                        {
                        text: "No",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => closeModal(),
                        color: "primary",
                        variant: "contained",
                        },
                    ]}
                />

            </div>
        );


    }

);


